import { attr, hasMany } from "@ember-data/model";
import { service } from "@ember/service";
import type HealthCentre from "parog-web/models/health-centre";
import type MedicsPatient from "parog-web/models/medics-patient";
import type TermsOfUseService from "parog-web/services/terms-of-use";
import Creator from "./creator";
import type TherapeuticalArea from "./therapeutical-area";

export default class MedicModel extends Creator {
  // Roles that should not be shown on front
  static nonDisplayableRoles = [
    "contract_research_organisation",
    "third_party_server",
    "test_bot",
    "internal_server",
    "waiting",
    "nothing"
  ];

  @service declare termsOfUse: TermsOfUseService;

  @attr("number") declare acceptedTermsOfUseVersion: number;
  @attr("string") declare address: string;
  @attr("string") declare aphCode: string;
  @attr({
    defaultValue() {
      return {};
    }
  })
  declare associatedData: Record<string, unknown>;
  @attr("boolean") declare canUseDmpRetrievalUpperLimit: boolean;
  @attr("string") declare city: string;
  @attr("date") declare createdAt: Date;
  @attr() declare days: string[];
  @attr() declare disabledAlertTypes: string[];
  @attr() declare disabledNotificationTypes: string[];
  @attr("string") declare dmpAdapterName: string;
  @attr("string") declare email: string;
  @attr("boolean") declare enableSentryReplay: boolean;
  @attr("string") declare fax: string;
  @attr("string") declare forceFinessForCnamRetrieval: string;
  @attr("string") declare forceIdentifierForInsRetrieval: string;
  @hasMany("health-centre", { async: false, inverse: "medics" })
  declare healthCentres: HealthCentre[];
  @attr("string") declare healthInsuranceNumber: string;
  // IDNPS is the Identifiant National de Professionnel de Santé
  @attr("string") declare idnps: string;
  @attr("boolean") declare insAccessDisabled: boolean;
  @attr("boolean") declare isInDiabetowiseTreatmentGroup: boolean;
  isMedic = true;
  @attr("boolean") declare isSuperUser: boolean;
  @attr("date") declare lastDmpDocumentsSearchDate: Date;
  @hasMany("medics-patient", { async: false, inverse: "medic" })
  declare medicsPatients: MedicsPatient[];
  @attr("date") declare passwordChangedAt: Date;
  @attr("string") declare phone: string;
  @attr("string") declare postcode: string;
  @attr("string") declare practiceLocation?: string;
  @attr("string") declare practiceSetting: string;
  @attr("number") declare preferredTime: number;
  @attr("string") declare professionalCategory: string;
  @attr("string") declare professionalId: string;
  @attr("boolean") declare refreshTokensEnabled: boolean;
  @attr("string") declare role: string;
  @attr("string") declare secureEmail: string;
  @attr() declare specialities: string[];
  @attr("string") declare status: string;
  @attr("boolean") declare twoFactorAuthenticationEnabled: boolean;
  /** A flag to activate medics fetch on /api/v2 */
  @attr("boolean") declare useMedicsV2: boolean;

  get bioSenderDisplayed(): boolean {
    return this.healthCentres.some(hc => hc.bioSenderDisplayed);
  }

  get enableCommentsOnNotifications(): boolean {
    return this.healthCentres.some(hc => hc.enableCommentsOnNotifications);
  }

  get followUpReasonIdsFromHealthCentres(): string[] {
    return [
      ...new Set(
        this.therapeuticalAreas
          .map(ta => ta?.followUpReasons.map(fur => fur.id))
          .flat()
      )
    ] as string[];
  }

  get initials(): string {
    return [this.firstName?.charAt(0), this.lastName?.charAt(0)]
      .join("")
      .trim();
  }

  get isAcceptedTermsOfUse(): boolean {
    return this.acceptedTermsOfUseVersion == this.termsOfUse.currentVersion;
  }

  get lastPasswordEditDate(): Date {
    return this.passwordChangedAt || this.createdAt;
  }

  get lgcEnabled(): boolean {
    return this.healthCentres.some(hc => hc.lgcEnabled);
  }

  get primaryHealthCenter(): HealthCentre | undefined {
    return this.healthCentres[0];
  }

  get seesImportantBioResultsSection() {
    return this.healthCentres.some(hc => hc.showImportantBioResultsSection);
  }

  get shouldChangePassword(): boolean {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    return this.lastPasswordEditDate < oneYearAgo;
  }

  get therapeuticalArea(): TherapeuticalArea | null {
    return this.primaryHealthCenter?.therapeuticalArea ?? null;
  }

  get therapeuticalAreaIds() {
    return this.therapeuticalAreas.map(area => area?.id);
  }

  get therapeuticalAreas() {
    return this.healthCentres.map(hc => hc.therapeuticalArea);
  }

  /**
   * Check if the current user is one of the given roles.
   * @return true if the current user is one of the roles passed as parameter
   * @example
   * medic.hasRole("doctor", "contract_research_organisation", "nurse_lead")
   */
  hasRole(...args: Array<string>): boolean {
    return args.includes(this.role);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    medic: MedicModel;
  }
}
